/**
 * Builds API-ready SellerSubmissionPayload from form input + attribution (Sell Your Car M2).
 */
import { SELLER_PRIVACY_DEFAULTS } from "./sellerIntakeConstants";
import { normalizeSellerSubmissionInput } from "./sellerIntakeNormalization";
import { resolveSubmissionIntlContext } from "../../intl/submissionContext.js";

function pickNullableString(value) {
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
}

function detectLocale() {
  return resolveSubmissionIntlContext().locale;
}

function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * @param {import('./sellerIntakeTypes').SellerSubmissionInput|object} input
 * @param {import('./sellerIntakeTypes').SellerAttributionContext|object} [attribution]
 * @param {{
 *   submittedAt?: string,
 *   privacyPolicyVersion?: string,
 *   submissionToken?: string|null,
 *   photoCount?: number,
 * }} [options]
 * @returns {import('./sellerIntakeTypes').SellerSubmissionPayload}
 */
export function buildSellerSubmissionPayload(input = {}, attribution = {}, options = {}) {
  const normalized = normalizeSellerSubmissionInput(input);
  const submittedAt = options.submittedAt || new Date().toISOString();
  const photoCount = options.photoCount ?? (normalized.hasPhotos ? 0 : 0);

  /**
   * consentAcceptedAt: client may send a preview timestamp when consent is checked.
   * The API/server must set the authoritative consentAcceptedAt on persist.
   */
  const consentAcceptedAt = normalized.consentAccepted ? submittedAt : null;
  const intlContext = resolveSubmissionIntlContext(options.intlContext);

  return {
    seller: {
      type: normalized.sellerType,
      name: normalized.sellerName,
      phone: pickNullableString(normalized.sellerPhone),
      email: pickNullableString(normalized.sellerEmail),
      preferredContactMethod: normalized.preferredContactMethod,
      city: normalized.city,
      department: pickNullableString(normalized.department),
    },
    vehicle: {
      make: normalized.vehicleMake,
      model: normalized.vehicleModel,
      year: normalized.vehicleYear,
      trim: pickNullableString(normalized.vehicleTrim),
      mileageKm: normalized.vehicleMileageKm,
      plate: pickNullableString(normalized.plate),
      vin: pickNullableString(normalized.vin),
      ownershipStatus: normalized.ownershipStatus,
    },
    pricing: {
      askingPrice: normalized.askingPrice,
      currency: normalized.currency,
    },
    condition: {
      summary: pickNullableString(normalized.conditionSummary),
      notes: pickNullableString(normalized.notes),
      hasPhotos: normalized.hasPhotos,
      photoCount,
    },
    privacy: {
      consentAccepted: normalized.consentAccepted,
      consentAcceptedAt,
      privacyPolicyVersion:
        options.privacyPolicyVersion ||
        SELLER_PRIVACY_DEFAULTS.privacyPolicyVersion,
      marketingOptIn: normalized.marketingOptIn,
    },
    attribution: {
      sourcePage: attribution.sourcePage || null,
      sourceComponent: attribution.sourceComponent || null,
      sourceAction: attribution.sourceAction || null,
      currentPath: attribution.currentPath || null,
      referrer: attribution.referrer || null,
      utmSource: attribution.utmSource || null,
      utmMedium: attribution.utmMedium || null,
      utmCampaign: attribution.utmCampaign || null,
      utmContent: attribution.utmContent || null,
      utmTerm: attribution.utmTerm || null,
    },
    meta: {
      locale: intlContext.locale,
      language: intlContext.language,
      displayCurrency: intlContext.displayCurrency,
      routeLocale: intlContext.routeLocale,
      clientViewSlug: intlContext.clientViewSlug,
      timezone: detectTimezone(),
      clientSubmittedAt: submittedAt,
      submissionToken: options.submissionToken || null,
      honeypot: normalized.honeypot,
    },
  };
}
